import React, { Fragment, useState, useEffect, useMemo, useCallback } from "react";
import { useBookingContext } from "../context/BookingContext";
import { useTranslation } from "react-i18next";
import Modal from "react-responsive-modal";
import moment from "moment";
import AdditionalSettingCheckbox from "./AdditionalSettingCheckbox";
import AdditionalSettingCounter from "./AdditionalSettingCounter";

const ITEM_CONFIG = {
  umbrella: {
    labelKey: "umbrella",
    icon: "fa-umbrella",
    descriptionKey: "weather_protection",
    type: "counter",
  },
  baby_seat: {
    labelKey: "baby_seat",
    icon: "fa-baby-carriage",
    descriptionKey: "safety_seat_children",
    type: "counter",
  },
  phone_holder: {
    labelKey: "phone_holder",
    icon: "fa-mobile-alt",
    descriptionKey: "phone_holder_desc",
    defaultDescription: "Secure phone mount for navigation",
    type: "counter",
  },
  gps: {
    labelKey: "gps",
    icon: "fa-location-arrow",
    descriptionKey: "gps_desc",
    defaultDescription: "GPS navigation device",
    type: "counter",
  },
  insurance: {
    labelKey: "insurance",
    icon: "fa-shield-halved",
    descriptionKey: "full_covered",
    type: "checkbox",
  },
  wedding: {
    labelKey: "wedding",
    icon: "fa-asl-interpreting",
    descriptionKey: "beautiful_flower_arrangements",
    type: "checkbox",
  },
  additional_stop: {
    labelKey: "additional_stop",
    icon: "fa-map-pin",
    descriptionKey: "extra_stops",
    type: "checkbox",
  },
  toll_and_parking: {
    labelKey: "toll_and_parking",
    icon: "fa-box-open",
    descriptionKey: "toll_and_parking_desc",
    type: "checkbox",
  },
  // all_in_one_additional_items: {
  //   labelKey: "all_in_one_for",
  //   icon: "fa-box-open",
  //   descriptionKey: "all_in_one_package",
  //   type: "checkbox",
  // },
  flower_bouquet: {
    labelKey: "flower_bouquet",
    icon: "fa-spa",
    descriptionKey: "fresh_flower_bouquet",
    type: "checkbox",
  },
};

// Item yang selalu ada
const BASE_ITEMS = ["baby_seat"];

// Item berdasarkan tipe layanan
const ITEMS_BY_SERVICE_TYPE = {
  self_drive: ["umbrella", "phone_holder", "gps"],
  transfer_service: ["additional_stop", "flower_bouquet", "toll_and_parking"],
  tour: ["toll_and_parking"],
  default: ["wedding"],
};

// Item dengan tipe counter (menggunakan quantity)
const COUNTER_ITEMS = ["baby_seat", "umbrella", "phone_holder", "gps"];

function AdditionalChargeByDate({ formik, data }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);

  const { setAditionalItem } = useBookingContext();
  const { t } = useTranslation();

  const values = formik.values?.additional_items || [];

  // Generate list item berdasarkan tipe layanan
  const listAdditionalCharges = useMemo(() => {
    const items = [...BASE_ITEMS];
    const serviceType = data?.type;

    if (serviceType === "self_drive") {
      items.push(...ITEMS_BY_SERVICE_TYPE.self_drive);
    }

    if (serviceType === "transfer_service") {
      items.push(...ITEMS_BY_SERVICE_TYPE.transfer_service);
    } else {
      items.push(...ITEMS_BY_SERVICE_TYPE.default);
    }

    if (serviceType === "tour") {
      items.push("toll_and_parking");
    }

    return [...new Set(items)]; // Hapus duplikat
  }, [data?.type]);

  // Helper functions menggunakan memoization
  const createLabel = useCallback((itemKey) => t(ITEM_CONFIG[itemKey]?.labelKey || ""), [t]);

  const createIcon = useCallback((itemKey) => ITEM_CONFIG[itemKey]?.icon || "", []);

  const createDescription = useCallback(
    (itemKey) => {
      const config = ITEM_CONFIG[itemKey];
      if (!config) return "";

      const translated = t(config.descriptionKey);
      if (translated !== config.descriptionKey) return translated;

      return config.defaultDescription || "";
    },
    [t]
  );

  // Filter data untuk item tertentu
  const getFilteredDates = useCallback(
    (itemKey) => {
      const isCounter = COUNTER_ITEMS.includes(itemKey);

      return values
        .filter((val) => (isCounter ? val[itemKey] > 0 : val[itemKey] === true))
        .map((v) => moment(v.date).format("DD MMM"));
    },
    [values]
  );

  // Effect untuk update context
  useEffect(() => {
    setAditionalItem(values);
  }, [values, setAditionalItem]);

  // Handler untuk membuka modal
  const handleOpenModal = useCallback((itemKey, title) => {
    setSelected({ value: itemKey, title });
    setOpen(true);
  }, []);

  // Handler untuk menutup modal
  const handleCloseModal = useCallback(() => {
    setOpen(false);
  }, []);

  // Handler untuk toggle expand
  const handleToggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // Cek apakah item adalah counter
  const isCounterItem = useCallback((itemKey) => COUNTER_ITEMS.includes(itemKey), []);

  return (
    <Fragment>
      <div className="overflow-hidden bg-gradient-to-br from-slate-50 to-white">
        {/* Header */}
        <button
          type="button"
          onClick={handleToggleExpand}
          className="flex items-center justify-between w-full py-4 transition-all duration-300 bg-white hover:bg-slate-50"
          aria-expanded={isExpanded}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#f5272b]/10 rounded-xl flex items-center justify-center">
              <i className="fa-solid fa-layer-group text-[#f5272b]" aria-hidden="true"></i>
            </div>
            <h3 className="text-lg font-bold text-slate-900">{t("additional_items")}</h3>
          </div>
          <i
            className={`fa-solid fa-chevron-down text-[#f5272b] text-lg transition-transform duration-300 ${
              isExpanded ? "rotate-180" : ""
            }`}
            aria-hidden="true"
          ></i>
        </button>

        {/* Content */}
        <div
          className={`transition-all duration-500 overflow-hidden ${
            isExpanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="py-6 space-y-4 bg-gradient-to-br from-slate-50 to-white">
            {listAdditionalCharges.map((itemKey, idx) => {
              const title = createLabel(itemKey);
              const icon = createIcon(itemKey);
              const description = createDescription(itemKey);
              const selectedDates = getFilteredDates(itemKey);
              const hasSelection = selectedDates.length > 0;

              return (
                <div
                  key={`${itemKey}-${idx}`}
                  className={`bg-white rounded-sm p-5 border transition-all duration-300 ${
                    hasSelection
                      ? "border-green-300 shadow-green-100"
                      : "border-slate-200 hover:border-[#f5272b]/30 hover:shadow-md"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Icon & Info */}
                    <div className="flex items-start flex-1 min-w-0 gap-4">
                      <div
                        className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
                          hasSelection ? "bg-green-500" : "bg-slate-100"
                        }`}
                      >
                        <i
                          className={`fa-solid ${icon} text-xl ${hasSelection ? "text-white" : "text-slate-600"}`}
                          aria-hidden="true"
                        ></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="mb-1 text-base font-bold text-slate-900">{title}</h4>
                        <p className="mb-3 text-sm text-slate-600">{description}</p>

                        {/* Selected Dates */}
                        {hasSelection && (
                          <div className="flex flex-wrap gap-2">
                            {selectedDates.map((date, dateIdx) => (
                              <span
                                key={`${itemKey}-date-${dateIdx}`}
                                className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-semibold border border-green-200"
                              >
                                <i className="fa-solid fa-calendar-check" aria-hidden="true"></i>
                                {date}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Button */}
                    <button
                      type="button"
                      onClick={() => handleOpenModal(itemKey, title)}
                      className={`flex-shrink-0 px-5 py-2.5 font-semibold rounded-sm transition-all duration-300 whitespace-nowrap ${
                        hasSelection
                          ? "bg-green-500 hover:bg-green-600 text-white"
                          : "bg-[#f5272b] hover:bg-red-700 text-white"
                      }`}
                    >
                      <i className={`mr-2 fa-solid ${hasSelection ? "fa-pen" : "fa-plus"}`} aria-hidden="true"></i>
                      {hasSelection ? t("change") : t("add")}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Modal */}
      <Modal
        open={open}
        onClose={handleCloseModal}
        styles={{
          root: { zIndex: 9999999991 },
          modal: {
            borderRadius: "4px",
            padding: 0,
            maxWidth: "500px",
            width: "95%",
          },
        }}
        closeOnOverlayClick
      >
        {selected &&
          (isCounterItem(selected.value) ? (
            <AdditionalSettingCounter formik={formik} selected={selected} onClose={handleCloseModal} />
          ) : (
            <AdditionalSettingCheckbox formik={formik} selected={selected} onClose={handleCloseModal} />
          ))}
      </Modal>
    </Fragment>
  );
}

export default React.memo(AdditionalChargeByDate);
